/**
 * @file src/config/profileOptions.js
 * @description The small closed sets a profile asks for.
 *
 * These were free-text boxes. A blank field does not say what is wanted, so
 * everybody invents their own answer — "Mr", "mr.", "MR", "Mr." are four
 * strings and one title — and anything downstream that has to group, match or
 * address people on them gets four buckets where there should be one.
 *
 * Kept as data in one file rather than inline in the page so the same lists
 * can back an admin edit form or a signup step without a second copy drifting
 * from this one.
 */

/**
 * Titles.
 *
 * Deliberately short. A long list of honorifics is worse than a short one
 * plus "Other" — it pushes the common cases below the fold, and the tail is
 * endless anyway. Mx is included because leaving it out forces a choice
 * nobody should have to make to fill in a form.
 */
export const TITLE_OPTIONS = Object.freeze([
  { value: 'Mr', label: 'Mr' },
  { value: 'Mrs', label: 'Mrs' },
  { value: 'Ms', label: 'Ms' },
  { value: 'Miss', label: 'Miss' },
  { value: 'Mx', label: 'Mx' },
  { value: 'Dr', label: 'Dr' },
  { value: 'Prof', label: 'Prof' },
  { value: 'Rev', label: 'Rev' },
  { value: 'Sir', label: 'Sir' },
  { value: 'Dame', label: 'Dame' },
  { value: 'Lord', label: 'Lord' },
  { value: 'Lady', label: 'Lady' }
]);

/**
 * Security questions.
 *
 * Chosen against three failure modes rather than picked for familiarity:
 *
 *   · GUESSABLE from a public profile. "What school did you go to" and
 *     "what is your mother's maiden name" are on LinkedIn and in public
 *     records respectively — the classic ones are the weak ones.
 *   · CHANGES over time. "What is your favourite film" gets a different
 *     answer in five years, and the account is locked by an honest reply.
 *   · NOT UNIVERSAL. "What was your first car" has no answer for someone who
 *     has never driven, and they either lie or pick another question.
 *
 * So these lean on fixed, private, specific facts. `custom` is last and
 * deliberate: any fixed list will fail somebody, and a free-text escape is
 * better than watching them pick a question they must invent an answer to.
 */
export const SECURITY_QUESTION_OPTIONS = Object.freeze([
  { value: 'first-pet', label: 'What was the name of your first pet?' },
  { value: 'childhood-street', label: 'What street did you live on as a child?' },
  { value: 'childhood-friend', label: 'What was the first name of your oldest childhood friend?' },
  { value: 'mother-first-name', label: "What is your mother's first name?" },
  { value: 'father-birth-town', label: 'In what town was your father born?' },
  { value: 'first-job', label: 'What was the name of your first employer?' },
  { value: 'first-concert', label: 'What was the first concert you attended?' },
  { value: 'grandparent-occupation', label: "What was your maternal grandfather's occupation?" },
  { value: 'childhood-nickname', label: 'What was your childhood nickname?' },
  { value: 'primary-teacher', label: 'What was the surname of your first teacher?' },
  { value: 'custom', label: 'Write my own question…' }
]);

/** The stored question text for a choice, so answers survive a label reword. */
export const labelForSecurityQuestion = (value) => (
  SECURITY_QUESTION_OPTIONS.find((option) => option.value === value)?.label || ''
);

/**
 * Fields that must be filled before a profile counts as complete.
 *
 * `firstName`/`lastName` because a review, an invoice and a message all have
 * to address somebody by name, and "there" is not a name. `title` is NOT
 * here — it is a courtesy, not information the platform needs, and demanding
 * it would block people whose answer is none of the options.
 */
export const REQUIRED_PROFILE_FIELDS = Object.freeze([
  { key: 'firstName', label: 'First name' },
  { key: 'lastName', label: 'Last name' }
]);

/**
 * How complete is this profile, and what is missing?
 *
 * Returns the gaps rather than a bare percentage: "60% complete" tells
 * somebody they are being judged without telling them what to do about it.
 */
export const profileCompletion = (about = {}) => {
  const missing = REQUIRED_PROFILE_FIELDS.filter(
    (field) => !String(about?.[field.key] || '').trim()
  );
  return {
    complete: missing.length === 0,
    missing,
    filled: REQUIRED_PROFILE_FIELDS.length - missing.length,
    total: REQUIRED_PROFILE_FIELDS.length
  };
};

export default TITLE_OPTIONS;
